// videoKucult.js — yükleme ÖNCESİ, telefonda video küçültme.
//
// İSTEK (kullanıcı, 23.09.2026): "kullanıcılarımızın en büyük şikayeti dosyayı
// yüklemek... fe kısmında yükleme işlemi öncesi küçültüp öyle yükleme olsun."
// Ayrıntılı gerekçe ve ölçümler: .agent/MEDYA-YUKLEME-KUCULTME.md
//
// ÇALIŞMA KURALI (kullanıcı kararı, tartışmasız):
//     küçültmeyi DENE → oluyorsa küçük dosya, olmuyorsa ORİJİNAL
//     her iki durumda da yükleme devam eder.
// Yani bu dosya YÜKLEMEYİ ASLA ENGELLEMEZ. Hata olursa, cihaz desteklemiyorsa,
// süre aşarsa — hepsinde orijinal dosya döner ve personel bir şey fark etmez.
// `videoKucult` bu yüzden HİÇBİR ZAMAN throw etmez.
//
// NEDEN ffmpeg.wasm DEĞİL: çalışmadan önce ~30 MB indirmesi gerekiyor. Hedef
// kitlemiz sahada KÖTÜ İNTERNETTE olan personel; çözmeye çalıştığımız sorunun
// aynısını baştan yaratırdı. Ayrıca SharedArrayBuffer için sayfaya COOP/COEP
// başlıkları gerekiyor, o da FE'de başka şeyleri bozabilir.
// Onun yerine WebCodecs: telefonun kendi video çipini kullanır.
//
// ÇIKTI MP4/H.264 OLMAK ZORUNDA. WebM üretseydik bağışçı tarafında ve
// WhatsApp'ta oynatma sorunları çıkardı.
//
// SES KORUNUYOR (kullanıcı kararı): kurban videosunda bağışçının adı sesli
// söyleniyor, ses bilgi taşıyor.

// KÜTÜPHANELER TEPEDE DEĞİL, İHTİYAÇ ANINDA YÜKLENİYOR (dinamik import).
// Sebebi test değil, SAHA: mp4box açılımı ~2 MB. Tepeden import edilseydi
// uygulamayı açan HERKES bunu indirirdi — video yüklemeyecek olan bile.
// Hedef kitlemiz kötü internetteki personel; onlara sayfa açılışında 2 MB
// fazladan yüklemek, çözmeye çalıştığımız sorunun aynısı olurdu.
// Böylece paketler yalnızca gerçekten küçültme yapılacağı an iniyor.
const kutuphaneleriYukle = async () => {
  const [mp4box, muxer] = await Promise.all([import("mp4box"), import("mp4-muxer")]);
  return {
    createFile: mp4box.createFile,
    DataStream: mp4box.DataStream,
    MP4BoxBuffer: mp4box.MP4BoxBuffer,
    Muxer: muxer.Muxer,
    ArrayBufferTarget: muxer.ArrayBufferTarget,
  };
};

// ---------------------------------------------------------------------------
// Ayarlar
// ---------------------------------------------------------------------------

export const VARSAYILAN = {
  // Kullanıcı kararı: 480p. Kötü internette yükleme süresini 720p'ye göre
  // kabaca yarıya indiriyor.
  hedefYukseklik: 480,
  // Bu boyutun altındaki videoya dokunulmaz — kazanç zahmete değmez ve
  // yeniden kodlamak kaliteyi boşuna düşürür.
  esikBayt: 2 * 1024 * 1024,
  // Uzun videoda cozulmus kareler telefonun bellegini zorlar.
  enUzunSaniye: 6 * 60,
  // Telefon zorlanıyorsa bırak. Personeli donmuş ekranla bekletmek,
  // yavaş yüklemeden daha kötü.
  zamanAsimiMs: 3 * 60 * 1000,
  // Video bit hızı (480p için makul bir değer).
  videoBitHizi: 1000000,
};

// ---------------------------------------------------------------------------
// SAF yardımcılar — tarayıcı API'si kullanmazlar, test edilebilirler.
// ---------------------------------------------------------------------------

/** Dosya video mu? */
export const videoMu = (dosya) => /^video\//i.test(dosya?.type || "");

/**
 * Cihaz küçültmeyi yapabilir mi? WebCodecs eski Android ve eski iOS'ta yok.
 * Yokluğu HATA DEĞİL, normal bir durum — orijinal yüklenir.
 */
export const desteklenirMi = (k = typeof window !== "undefined" ? window : {}) =>
  typeof k.VideoEncoder === "function" &&
  typeof k.VideoDecoder === "function" &&
  typeof k.VideoFrame === "function" &&
  typeof k.OffscreenCanvas === "function";
// AudioEncoder ARANMIYOR (30.09.2026): ses yeniden kodlanmiyor, oldugu gibi
// aktariliyor. Masaustu Linux Chrome'da AAC KODLAMA desteklenmiyor
// (`mp4a.40.2 DESTEKLENMIYOR` — headless Chromium ile olculdu); AudioEncoder'a
// bagli kalsaydik kucultme o platformlarda hep sessizce geri duserdi.
// Zaten olan da buydu: 129 MB dosya 129 MB olarak yuklendi.

/**
 * Küçültme denensin mi? Denenmeyecekse SEBEBİ de döner — log'a yazılıp
 * sahada neyin neden olmadığı anlaşılabilsin diye.
 */
export const kucultmeKarari = (dosya, ayar = VARSAYILAN, ortam) => {
  if (!dosya) return { denensin: false, sebep: "dosya yok" };
  if (!videoMu(dosya)) return { denensin: false, sebep: "video degil" };
  if (!desteklenirMi(ortam)) return { denensin: false, sebep: "cihaz WebCodecs desteklemiyor" };
  if (dosya.size <= ayar.esikBayt) return { denensin: false, sebep: "zaten kucuk" };
  return { denensin: true, sebep: null };
};

/**
 * Hedef ölçü. En-boy oranı korunur, hedeften ZATEN küçükse büyütülmez.
 * Boyutlar ÇİFT sayıya yuvarlanır: H.264 tek sayılı boyutlarda kodlayıcıyı
 * patlatır veya yeşil şeritler bırakır.
 */
export const hedefOlcu = (genislik, yukseklik, hedefYukseklik = VARSAYILAN.hedefYukseklik) => {
  if (!genislik || !yukseklik) return null;
  const oran = yukseklik <= hedefYukseklik ? 1 : hedefYukseklik / yukseklik;
  const cift = (n) => Math.max(2, Math.round((n * oran) / 2) * 2);
  return { genislik: cift(genislik), yukseklik: cift(yukseklik) };
};

/**
 * Sonuç işe yaradı mı? Kodlama bazen dosyayı BÜYÜTÜR (zaten sıkışık kaynak,
 * kısa video). Kazanç belirgin değilse orijinali tutmak daha doğru — hem
 * kalite korunur hem boşuna işlem yapmış olmayız.
 */
export const kazancliMi = (orijinalBayt, yeniBayt, enAzKazanc = 0.1) =>
  yeniBayt > 0 && yeniBayt <= orijinalBayt * (1 - enAzKazanc);

/**
 * Kaynak videonun DONME acisi (saat yonunde, derece).
 *
 * NEDEN GEREKLI: Telefon videolari genelde YATAY kodlanir; dik gosterilmelerini
 * saglayan sey tkhd kutusundaki donusum matrisidir. Kareleri cozup yeniden
 * kodlarken bu bilgi tasinmazsa video YAN DONER — kullanicinin bildirdigi hata
 * tam olarak buydu (30.09.2026).
 *
 * Matris 16.16 sabit noktali; yalnizca a,b,c,d bileseni aci tasir.
 * ffprobe ile dogrulanan eslesme:
 *     a= 1 b= 0  ->   0
 *     a= 0 b=-1  ->  90
 *     a=-1 b= 0  -> 180
 *     a= 0 b= 1  -> 270
 * Formul: saat yonu aci = -atan2(b, a), 0-360 araligina normalize.
 */
export const donmeAcisi = (matrix) => {
  if (!matrix) return 0;
  const d = Array.isArray(matrix) ? matrix : Object.values(matrix);
  if (d.length < 2) return 0;
  const a = Number(d[0]) / 65536;
  const b = Number(d[1]) / 65536;
  if (!Number.isFinite(a) || !Number.isFinite(b) || (a === 0 && b === 0)) return 0;
  const ham = -Math.round((Math.atan2(b, a) * 180) / Math.PI);
  const aci = ((ham % 360) + 360) % 360;
  // mp4-muxer yalnizca 0/90/180/270 kabul eder; arasi bir deger gelirse
  // (bozuk matris) donme uygulamamak, yanlis donme uygulamaktan iyidir.
  return [0, 90, 180, 270].includes(aci) ? aci : 0;
};

/**
 * Goruntuleme acisini mp4-muxer'in bekledigi degere cevirir.
 *
 * DIKKAT — YONLER ZIT. Olculdu (30.09.2026), tahmin degil:
 *     mp4-muxer rotation:90  -> matris a=0 b= 1   (ffmpeg'in 270 derecesi)
 *     mp4-muxer rotation:270 -> matris a=0 b=-1   (ffmpeg'in  90 derecesi)
 * Kaynaktaki matrisi aynen yeniden uretmek icin aci TERS cevrilmeli.
 * Ilk denemede ters cevrilmedigi icin video dik yerine TERS yone dondu.
 */
export const muxerDonmesi = (aci) => (360 - (Number(aci) || 0)) % 360;

/** mp4box zaman birimi (timescale) -> WebCodecs zaman birimi (mikrosaniye). */
export const mikrosaniye = (deger, timescale) =>
  timescale ? Math.round((deger * 1e6) / timescale) : 0;

// ---------------------------------------------------------------------------
// Demux — mp4box ile MP4 kutusunu açıp görüntü izini çıkarma
// ---------------------------------------------------------------------------

// Kodlayıcı ayarı (avcC/hvcC kutusu) VideoDecoder'a "description" olarak
// verilmek zorunda; bu olmadan H.264 çözülemez.
const kodlayiciTanimi = (isoDosya, izId, DataStream) => {
  const trak = isoDosya.getTrackById(izId);
  for (const girdi of trak?.mdia?.minf?.stbl?.stsd?.entries || []) {
    const kutu = girdi.avcC || girdi.hvcC || girdi.vpcC || girdi.av1C;
    if (!kutu) continue;
    const akis = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
    kutu.write(akis);
    // İlk 8 bayt kutu başlığı (boyut + tip); WebCodecs bunu istemiyor.
    return new Uint8Array(akis.buffer, 8);
  }
  return null;
};

/**
 * AAC "AudioSpecificConfig" baytlari. Ses izini YENIDEN KODLAMADAN aktarmak
 * icin muxer'a bu verilmek zorunda; olmadan ses izi okunamaz hale gelir.
 * esds kutusunun icinde tag=5 olan tanimlayicida duruyor.
 */
const sesTanimi = (isoDosya, izId) => {
  const trak = isoDosya.getTrackById(izId);
  for (const girdi of trak?.mdia?.minf?.stbl?.stsd?.entries || []) {
    const kok = girdi.esds?.esd;
    if (!kok) continue;
    const ara = (d) => {
      if (!d) return null;
      if (d.tag === 5 && d.data) return new Uint8Array(d.data);
      for (const alt of d.descs || []) {
        const b = ara(alt);
        if (b) return b;
      }
      return null;
    };
    const b = ara(kok);
    if (b) return b;
  }
  return null;
};

// Zaman damgalarini SIFIRDAN baslatir.
// Kaynak videonun en erken karesi her zaman 0'da degil (kodlayici gecikmesi /
// edit list yuzunden 200 ms gibi bir kayma olabiliyor — fixture'da tam bu var).
// Her iz KENDI en kucugune gore kaydiriliyor ki ses ile goruntu ayni anda
// baslasin. EN KUCUK aliniyor, ILK ORNEGIN degeri degil: B-kareli videoda
// ornekler cozme sirasinda gelir, ilk ornek en erken kare olmayabilir; ilk
// ornege gore kaydirsaydik bazi damgalar NEGATIF cikardi.
const ornekleriDuzenle = (ham) => {
  let enKucuk = Infinity;
  for (const o of ham) {
    const z = mikrosaniye(o.cts, o.timescale);
    if (z < enKucuk) enKucuk = z;
  }
  if (!Number.isFinite(enKucuk)) enKucuk = 0;
  return ham.map((o) => ({
    veri: o.data,
    anahtar: o.is_sync,
    zaman: mikrosaniye(o.cts, o.timescale) - enKucuk,
    sure: mikrosaniye(o.duration, o.timescale),
  }));
};

/**
 * Dosyayi ayristirip GORUNTU ve SES izlerini birlikte dondurur.
 * Ses izi yeniden kodlanmayacak, oldugu gibi aktarilacak.
 */
export const iziCoz = (arrayBuffer, { createFile, DataStream, MP4BoxBuffer }) =>
  new Promise((coz, red) => {
    const isoDosya = createFile();
    let videoIz = null;
    let sesIz = null;
    const kova = new Map(); // iz id -> ham ornekler

    isoDosya.onError = (e) => red(new Error(`mp4box: ${e}`));
    isoDosya.onReady = (bilgi) => {
      videoIz = bilgi.videoTracks?.[0] || null;
      sesIz = bilgi.audioTracks?.[0] || null;
      if (!videoIz) return red(new Error("video izi bulunamadi"));
      for (const iz of [videoIz, sesIz]) {
        if (!iz) continue;
        kova.set(iz.id, []);
        isoDosya.setExtractionOptions(iz.id, null, { nbSamples: Infinity });
      }
      isoDosya.start();
    };
    // onSamples birden cok kez cagrilabilir; biriktirip flush sonrasi
    // degerlendiriyoruz. push(...dizi) buyuk dosyada yigin tasirir, dongu.
    isoDosya.onSamples = (id, _kullanici, ornekler) => {
      const a = kova.get(id);
      if (!a) return;
      for (const o of ornekler) a.push(o);
    };

    try {
      // mp4box 2.x `appendBuffer` icin MP4BoxBuffer ister.
      isoDosya.appendBuffer(MP4BoxBuffer.fromArrayBuffer(arrayBuffer, 0));
      isoDosya.flush();
    } catch (e) {
      return red(new Error(`mp4box ayristirma: ${e.message}`));
    }

    if (!videoIz) return red(new Error("video izi bulunamadi"));
    const videoOrnek = kova.get(videoIz.id) || [];
    if (!videoOrnek.length) return red(new Error("video ornegi cikarilamadi"));

    const sesOrnek = sesIz ? kova.get(sesIz.id) || [] : [];
    coz({
      iz: videoIz,
      donme: donmeAcisi(videoIz.matrix),
      tanim: kodlayiciTanimi(isoDosya, videoIz.id, DataStream),
      ornekler: ornekleriDuzenle(videoOrnek),
      ses: sesIz && sesOrnek.length
        ? { iz: sesIz, tanim: sesTanimi(isoDosya, sesIz.id), ornekler: ornekleriDuzenle(sesOrnek) }
        : null,
    });
  });

// ---------------------------------------------------------------------------
// Ana iş
// ---------------------------------------------------------------------------

const kucult = async (dosya, ayar, ilerleme, signal, asama) => {
  const kutuphane = await kutuphaneleriYukle();
  const { Muxer, ArrayBufferTarget } = kutuphane;
  const arrayBuffer = await dosya.arrayBuffer();
  const { iz, donme, tanim, ornekler, ses } = await iziCoz(arrayBuffer, kutuphane);

  const sureSn = iz.movie_duration / (iz.movie_timescale || 1);
  if (sureSn > ayar.enUzunSaniye) throw new Error(`video cok uzun (${Math.round(sureSn)} sn)`);

  const olcu = hedefOlcu(
    iz.track_width || iz.video?.width,
    iz.track_height || iz.video?.height,
    ayar.hedefYukseklik
  );
  if (!olcu) throw new Error("video olculeri okunamadi");


  const hedef = new ArrayBufferTarget();
  const muxer = new Muxer({
    target: hedef,
    fastStart: "in-memory", // moov başa alınır: telefonda/tarayıcıda anında oynar
    // DONME KAYNAKTAN AKTARILIYOR. Pikselleri cevirmiyoruz, yalnizca bilgiyi
    // tasiyoruz — oynatici zaten bu bilgiye gore ceviriyor. Tasinmazsa video
    // yan doner.
    video: { codec: "avc", width: olcu.genislik, height: olcu.yukseklik, rotation: muxerDonmesi(donme) },
    // Ses izi KAYNAKTAN oldugu gibi aktariliyor; codec/kanal/hiz da oradan.
    ...(ses
      ? {
          audio: {
            codec: "aac",
            numberOfChannels: ses.iz.audio?.channel_count || 1,
            sampleRate: ses.iz.audio?.sample_rate || 44100,
          },
        }
      : {}),
  });

  const tuval = new window.OffscreenCanvas(olcu.genislik, olcu.yukseklik);
  const boya = tuval.getContext("2d");

  const kodlayici = new window.VideoEncoder({
    output: (parca, meta) => muxer.addVideoChunk(parca, meta),
    error: (e) => {
      throw e;
    },
  });
  kodlayici.configure({
    codec: "avc1.42001f", // H.264 Baseline 3.1 — en geniş uyumluluk
    width: olcu.genislik,
    height: olcu.yukseklik,
    bitrate: ayar.videoBitHizi,
    framerate: 30,
  });

  let islenen = 0;
  const cozucu = new window.VideoDecoder({
    output: (kare) => {
      boya.drawImage(kare, 0, 0, olcu.genislik, olcu.yukseklik);
      const yeniKare = new window.VideoFrame(tuval, {
        timestamp: kare.timestamp,
        duration: kare.duration,
      });
      kare.close();
      kodlayici.encode(yeniKare);
      yeniKare.close();
      islenen += 1;
      if (ilerleme && ornekler.length) ilerleme(Math.round((islenen / ornekler.length) * 100));
    },
    error: (e) => {
      throw e;
    },
  });
  cozucu.configure({
    codec: iz.codec,
    codedWidth: iz.video?.width,
    codedHeight: iz.video?.height,
    description: tanim,
  });

  for (const ornek of ornekler) {
    // Kucultme uzun surebilir; kullanici iptal ettiyse devam etmenin anlami yok.
    if (signal?.aborted) {
      try { cozucu.close(); kodlayici.close(); } catch { /* yoksay */ }
      throw new Error("iptal edildi");
    }
    cozucu.decode(
      new window.EncodedVideoChunk({
        type: ornek.anahtar ? "key" : "delta",
        timestamp: ornek.zaman,
        duration: ornek.sure,
        data: ornek.veri,
      })
    );
    // Geri basınç: kuyruk şişerse telefonun belleği dolar.
    if (cozucu.decodeQueueSize > 30) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 10));
    }
  }

  // SON ISLEMLER AYRI BIR ASAMA (01.10.2026).
  // Ilerleme son KARE cozuldugunde %100 oluyordu, ama is bitmiyordu: flush,
  // ses aktarimi ve finalize hala calisiyor. Kullanici olctu — cubuk %100'de
  // ~15 saniye asili kaldi ve ekran donmus gorundu. Artik bu asama soyleniyor.
  asama?.("sonlandiriliyor");
  await cozucu.flush();
  cozucu.close();
  await kodlayici.flush();
  kodlayici.close();

  // SES: yeniden kodlanmadan aktariliyor. Kodlayici gerekmiyor, kalite kaybi
  // yok, hizli. AudioSpecificConfig ILK parcada meta olarak verilmek zorunda;
  // olmadan muxer gecerli bir ses izi yazamaz.
  if (ses) {
    const meta = ses.tanim
      ? {
          decoderConfig: {
            codec: ses.iz.codec || "mp4a.40.2",
            numberOfChannels: ses.iz.audio?.channel_count || 1,
            sampleRate: ses.iz.audio?.sample_rate || 44100,
            description: ses.tanim,
          },
        }
      : undefined;
    ses.ornekler.forEach((o, i) => {
      muxer.addAudioChunkRaw(
        new Uint8Array(o.veri),
        o.anahtar ? "key" : "delta",
        o.zaman,
        o.sure,
        i === 0 ? meta : undefined
      );
    });
  }

  muxer.finalize();
  const ad = (dosya.name || "video").replace(/\.[^.]+$/, "") + ".mp4";
  return new File([hedef.buffer], ad, { type: "video/mp4", lastModified: Date.now() });
};

/**
 * Videoyu küçültmeyi dener. **ASLA throw ETMEZ.**
 * Her başarısızlıkta orijinal dosyayı döner — yükleme her hâlükârda devam eder.
 *
 * @param {File} dosya
 * @param {{ilerleme?:(yuzde:number)=>void, ayar?:object}} secenek
 * @returns {Promise<{dosya: File, kucultuldu: boolean, sebep: string|null}>}
 */
export async function videoKucult(dosya, secenek = {}) {
  const ayar = { ...VARSAYILAN, ...(secenek.ayar || {}) };
  const karar = kucultmeKarari(dosya, ayar);
  if (!karar.denensin) return { dosya, kucultuldu: false, sebep: karar.sebep };

  // Süre aşımı: telefon zorlanıyorsa bırak, orijinali yükle.
  let zamanlayici;
  const zamanAsimi = new Promise((_, red) => {
    zamanlayici = setTimeout(() => red(new Error("sure asildi")), ayar.zamanAsimiMs);
  });

  try {
    const yeni = await Promise.race([
      kucult(dosya, ayar, secenek.ilerleme, secenek.signal, secenek.asama),
      zamanAsimi,
    ]);
    if (!kazancliMi(dosya.size, yeni.size)) {
      return { dosya, kucultuldu: false, sebep: "kazanc yok" };
    }
    return { dosya: yeni, kucultuldu: true, sebep: null };
  } catch (e) {
    // Sahada bu satır önemli: neyin neden olmadığını sonradan anlayabilmek için.
    console.warn(`[videoKucult] kucultulemedi, orijinal yuklenecek: ${e?.message || e}`);
    return { dosya, kucultuldu: false, sebep: e?.message || "bilinmeyen hata" };
  } finally {
    clearTimeout(zamanlayici);
  }
}

export default videoKucult;
